/**
 * Contrôleur principal de l'éditeur de panneaux
 * Orchestre le formatage et le rendu en temps réel
 *
 * Intégration avec Shopify personnalisation-v2.liquid
 */

// Flag global pour désactiver l'ancien script
window.KONVA_EDITOR_ACTIVE = true;

class PanneauController {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.engine = null;
    this.renderer = null;
    this.rendererVerso = null;
    this.initialized = false;

    // Références aux éléments DOM
    this.elements = {
      textePrincipal: null,
      texteSecondaire: null,
      texteVerso: null,
      dimensionInputs: null,
      couleurInputs: null,
      previewContainer: null,
      previewContainerVerso: null,
      // Éléments pour le prix et le formulaire
      form: null,
      variantIdInput: null,
      priceElement: null,
      addToCartButton: null
    };

    // État actuel
    this.state = {
      texte_principal: '',
      texte_secondaire: '',
      texte_verso: '',
      dimension: 'rectangle',
      couleur_fond: 'Jaune',
      arrow_position: 'after',
      arrow_orientation: 0
    };

    // Debouncing
    this.updateTimeout = null;
    this.DEBOUNCE_DELAY = 300; // ms

    // Stockage des event handlers pour cleanup
    this._boundHandlers = {
      textePrincipalInput: null,
      texteSecondaireInput: null,
      texteVersoInput: null,
      formSubmit: null,
      dimensionChanges: new Map(),  // Map<input, handler>
      couleurChanges: new Map(),    // Map<input, handler>
      positionChanges: new Map(),   // Map<input, handler>
      orientationChanges: new Map() // Map<input, handler>
    };
  }

  /**
   * Détecte si une chaîne contient des emojis
   * @param {string} text - Le texte à vérifier
   * @returns {boolean} True si des emojis sont présents
   * @private
   */
  _containsEmoji(text) {
    // Regex pour les emojis cœur (acceptés)
    const heartEmojiRegex = /[\u{2763}-\u{2764}]|[\u{1F493}-\u{1F49F}]|[\u{1F5A4}]|[\u{1F90D}-\u{1F90E}]|[\u{1F9E1}]|[\u{1FA75}-\u{1FA77}]|\u{FE0F}/gu;

    // Retirer les emojis cœur du texte avant de vérifier
    const textWithoutHearts = text.replace(heartEmojiRegex, '');

    // Regex pour détecter les emojis (couvre la plupart des emojis Unicode)
    const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{231A}-\u{231B}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2614}-\u{2615}]|[\u{2648}-\u{2653}]|[\u{267F}]|[\u{2693}]|[\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26CE}]|[\u{26D4}]|[\u{26EA}]|[\u{26F2}-\u{26F3}]|[\u{26F5}]|[\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B50}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]|[\u{1F004}]|[\u{1F0CF}]|[\u{1F170}-\u{1F171}]|[\u{1F17E}-\u{1F17F}]|[\u{1F18E}]|[\u{1F191}-\u{1F19A}]|[\u{1F201}-\u{1F202}]|[\u{1F21A}]|[\u{1F22F}]|[\u{1F232}-\u{1F23A}]|[\u{1F250}-\u{1F251}]/gu;
    return emojiRegex.test(textWithoutHearts);
  }

  /**
   * Met à jour l'affichage de l'avertissement emoji pour un champ
   * @param {string} fieldId - L'identifiant du champ (texte_principal, texte_secondaire, texte_verso)
   * @param {string} text - Le texte à vérifier
   * @private
   */
  _updateEmojiWarning(fieldId, text) {
    const warningEl = document.getElementById(`emoji-warning-${fieldId}-${this.sectionId}`);
    if (warningEl) {
      if (this._containsEmoji(text)) {
        warningEl.classList.add('visible');
      } else {
        warningEl.classList.remove('visible');
      }
    }
  }

  /**
   * Initialise le contrôleur
   * @returns {Promise<boolean>} True si succès
   */
  async init() {
    // 1. Créer les instances engine et renderer
    this.engine = new RhinoFormattingEngine();
    const widthsLoaded = await this.engine.loadCharacterWidths();

    if (!widthsLoaded) {
      console.error('[PanneauEditor] Impossible de charger les largeurs de caractères');
      this._showError('Erreur de chargement des données de formatage');
      return false;
    }

    // 2. Trouver les éléments DOM
    if (!this._findElements()) {
      console.error('[PanneauEditor] Éléments DOM introuvables');
      return false;
    }

    // 3. Créer le conteneur de preview Konva (recto)
    if (!this._createPreviewContainer()) {
      console.error('[PanneauEditor] Impossible de créer le conteneur preview');
      return false;
    }

    // 4. Créer le conteneur de preview Konva (verso) si existe
    if (this.elements.previewContainerVerso) {
      this._createPreviewContainerVerso();
    }

    // 5. Initialiser le renderer (recto)
    this.renderer = new PanneauRenderer(`panneau-preview-${this.sectionId}`);
    await this.renderer.init(this.state.dimension);

    // 6. Initialiser le renderer verso (si existe)
    if (this.elements.previewContainerVerso) {
      this.rendererVerso = new PanneauRenderer(`panneau-preview-verso-${this.sectionId}`);
      await this.rendererVerso.init(this.state.dimension);
    }

    // 7. Attacher les événements
    this._attachEvents();

    // 8. Mettre à jour le prix initial
    this._updateVariantAndPrice();

    // 9. Marquer comme initialisé AVANT le premier rendu
    this.initialized = true;

    // 10. Premier rendu (maintenant que initialized = true)
    this.updatePreview();

    return true;
  }

  /**
   * Trouve tous les éléments DOM nécessaires
   * @private
   */
  _findElements() {
    this.elements.textePrincipal = document.getElementById(`texte_principal-${this.sectionId}`);
    this.elements.texteSecondaire = document.getElementById(`texte_secondaire-${this.sectionId}`);
    this.elements.texteVerso = document.getElementById(`texte_verso-${this.sectionId}`);
    this.elements.dimensionInputs = document.querySelectorAll(`input[name="properties[Dimension]"]`);
    this.elements.couleurInputs = document.querySelectorAll(`input[name="properties[Couleur de fond]"]`);
    this.elements.previewContainer = document.getElementById(`shape-${this.sectionId}`);
    this.elements.previewContainerVerso = document.getElementById(`shape-verso-${this.sectionId}`);

    // Éléments pour le prix et le formulaire
    this.elements.variantIdInput = document.getElementById(`product-variant-id-${this.sectionId}`);
    this.elements.priceElement = document.getElementById(`price-${this.sectionId}`);
    this.elements.form = document.getElementById(`personnalisation-form-${this.sectionId}`);
    if (this.elements.form) {
      this.elements.addToCartButton = this.elements.form.querySelector('button[type="submit"]');
    }

    // Vérifier que les éléments critiques existent
    return this.elements.textePrincipal && this.elements.previewContainer;
  }

  /**
   * Met à jour la variante produit et le prix en fonction de la configuration actuelle
   * @private
   */
  _updateVariantAndPrice() {
    // Trouver l'input dimension sélectionné pour récupérer les data-attributes
    const selectedDimensionInput = Array.from(this.elements.dimensionInputs || [])
      .find(input => input.checked);

    if (!selectedDimensionInput) {
      console.warn('[PanneauEditor] Aucune dimension sélectionnée');
      return;
    }

    // Déterminer la couleur (normaliser: Jaune -> jaune, Blanc -> blanc)
    const selectedColor = (this.state.couleur_fond || 'Jaune').toLowerCase();

    // Déterminer si on a du texte verso
    const hasVersoText = (this.state.texte_verso || '').trim().length > 0;

    // Construire la clé pour récupérer les attributs data
    const baseKey = selectedColor;  // "jaune" ou "blanc"
    const variantKey = hasVersoText ? `data-variant-${baseKey}-verso-id` : `data-variant-${baseKey}-id`;
    const priceKey = hasVersoText ? `data-variant-${baseKey}-verso-price-formatted` : `data-variant-${baseKey}-price-formatted`;

    const variantId = selectedDimensionInput.getAttribute(variantKey);
    const variantPriceFormatted = selectedDimensionInput.getAttribute(priceKey);

    if (variantId && variantPriceFormatted) {
      // Mettre à jour l'input hidden de variante
      if (this.elements.variantIdInput) {
        this.elements.variantIdInput.value = variantId;
      }

      // Mettre à jour le prix affiché
      if (this.elements.priceElement) {
        const regularPriceElement = this.elements.priceElement.querySelector('[data-regular-price]');
        if (regularPriceElement) {
          regularPriceElement.innerHTML = variantPriceFormatted;
        } else {
          this.elements.priceElement.innerHTML = variantPriceFormatted;
        }
      }

      // Injecter le prix dans le bouton (via data-price pour le CSS)
      if (this.elements.addToCartButton) {
        const buttonSpan = this.elements.addToCartButton.querySelector('span');
        if (buttonSpan) {
          buttonSpan.setAttribute('data-price', variantPriceFormatted);
        }
      }
    } else {
      console.warn('[PanneauEditor] Variante ou prix introuvable pour:', {
        couleur: selectedColor,
        verso: hasVersoText ? 'avec verso' : 'sans verso'
      });
    }
  }

  /**
   * Crée le conteneur pour le preview Konva (recto)
   * @private
   */
  _createPreviewContainer() {
    if (!this.elements.previewContainer) return false;

    // Créer un wrapper pour Konva
    const konvaWrapper = document.createElement('div');
    konvaWrapper.id = `panneau-preview-${this.sectionId}`;
    konvaWrapper.className = 'panneau-konva-preview';

    // VIDER le conteneur et ajouter uniquement Konva
    this.elements.previewContainer.innerHTML = '';
    this.elements.previewContainer.appendChild(konvaWrapper);

    return true;
  }

  /**
   * Crée le conteneur pour le preview Konva (verso)
   * @private
   */
  _createPreviewContainerVerso() {
    if (!this.elements.previewContainerVerso) return false;

    // Créer un wrapper pour Konva verso
    const konvaWrapperVerso = document.createElement('div');
    konvaWrapperVerso.id = `panneau-preview-verso-${this.sectionId}`;
    konvaWrapperVerso.className = 'panneau-konva-preview';

    // VIDER le conteneur et ajouter uniquement Konva
    this.elements.previewContainerVerso.innerHTML = '';
    this.elements.previewContainerVerso.appendChild(konvaWrapperVerso);

    return true;
  }

  /**
   * Attache les event listeners
   * @private
   */
  _attachEvents() {
    // Texte principal
    if (this.elements.textePrincipal) {
      // Initialiser l'état avec la valeur actuelle de l'input
      this.state.texte_principal = this.elements.textePrincipal.value || '';

      this._boundHandlers.textePrincipalInput = () => {
        this.state.texte_principal = this.elements.textePrincipal.value;
        this._updateEmojiWarning('texte_principal', this.state.texte_principal);
        this.updatePreviewDebounced();
      };
      this.elements.textePrincipal.addEventListener('input', this._boundHandlers.textePrincipalInput);
    }

    // Texte secondaire
    if (this.elements.texteSecondaire) {
      // Initialiser l'état avec la valeur actuelle de l'input
      this.state.texte_secondaire = this.elements.texteSecondaire.value || '';

      this._boundHandlers.texteSecondaireInput = () => {
        this.state.texte_secondaire = this.elements.texteSecondaire.value;
        this._updateEmojiWarning('texte_secondaire', this.state.texte_secondaire);
        this.updatePreviewDebounced();
      };
      this.elements.texteSecondaire.addEventListener('input', this._boundHandlers.texteSecondaireInput);
    }

    // Texte verso
    if (this.elements.texteVerso) {
      // Initialiser l'état avec la valeur actuelle de l'input
      this.state.texte_verso = this.elements.texteVerso.value || '';

      this._boundHandlers.texteVersoInput = () => {
        const previousHasVerso = (this.state.texte_verso || '').trim().length > 0;
        this.state.texte_verso = this.elements.texteVerso.value;
        const currentHasVerso = (this.state.texte_verso || '').trim().length > 0;

        this._updateEmojiWarning('texte_verso', this.state.texte_verso);

        // Si le statut verso change, mettre à jour le prix
        if (previousHasVerso !== currentHasVerso) {
          this._updateVariantAndPrice();
        }

        this.updatePreviewDebounced();
      };
      this.elements.texteVerso.addEventListener('input', this._boundHandlers.texteVersoInput);
    }

    // Dimension (rectangle/carré)
    if (this.elements.dimensionInputs) {
      // Référencer les éléments d'options de flèche
      const svgOptions = document.getElementById(`svg-options-${this.sectionId}`);
      const svgPositionOptions = document.getElementById(`svg-position-options-${this.sectionId}`);

      this.elements.dimensionInputs.forEach(input => {
        const handler = async () => {
          if (input.checked) {
            this.state.dimension = input.value;

            // Effet de fondu sortant sur les panneaux
            const visualsContainer = document.querySelector('.visuals-container');
            if (visualsContainer) {
              visualsContainer.style.opacity = '0';
            }

            // Afficher/masquer les options de flèche avec animation
            if (this.state.dimension === 'carré') {
              if (svgOptions) svgOptions.classList.add('kapie-hidden');
              if (svgPositionOptions) svgPositionOptions.classList.add('kapie-hidden');
            } else {
              if (svgOptions) svgOptions.classList.remove('kapie-hidden');
              if (svgPositionOptions) svgPositionOptions.classList.remove('kapie-hidden');
            }

            // Attendre la fin de l'animation de sortie
            await new Promise(resolve => setTimeout(resolve, 200));

            // Réinitialiser le renderer recto avec la nouvelle dimension
            this.renderer.destroy();
            this.renderer = new PanneauRenderer(`panneau-preview-${this.sectionId}`);
            await this.renderer.init(this.state.dimension);
            // Réinitialiser le renderer verso avec la nouvelle dimension
            if (this.rendererVerso) {
              this.rendererVerso.destroy();
              this.rendererVerso = new PanneauRenderer(`panneau-preview-verso-${this.sectionId}`);
              await this.rendererVerso.init(this.state.dimension);
            }
            // Mettre à jour la variante et le prix
            this._updateVariantAndPrice();
            this.updatePreview();

            // Effet de fondu entrant
            if (visualsContainer) {
              visualsContainer.style.opacity = '1';
            }
          }
        };
        this._boundHandlers.dimensionChanges.set(input, handler);
        input.addEventListener('change', handler);

        // Initialiser l'état et l'affichage des options de flèche
        if (input.checked) {
          this.state.dimension = input.value;
          // Appliquer l'état initial des options de flèche
          if (this.state.dimension === 'carré') {
            if (svgOptions) svgOptions.classList.add('kapie-hidden');
            if (svgPositionOptions) svgPositionOptions.classList.add('kapie-hidden');
          }
        }
      });
    }

    // Couleur de fond
    if (this.elements.couleurInputs) {
      const selectedColorSpan = document.getElementById(`selected-color-${this.sectionId}`);

      this.elements.couleurInputs.forEach(input => {
        const handler = async () => {
          if (input.checked) {
            // Effet de fondu sortant sur les panneaux
            const visualsContainer = document.querySelector('.visuals-container');
            if (visualsContainer) {
              visualsContainer.style.opacity = '0';
            }

            // Attendre la fin de l'animation de sortie
            await new Promise(resolve => setTimeout(resolve, 150));

            this.state.couleur_fond = input.value;
            // Mettre à jour le libellé de la couleur sélectionnée
            if (selectedColorSpan) {
              const label = document.querySelector(`label[for="${input.id}"]`);
              selectedColorSpan.textContent = label ? label.getAttribute('title') || input.value : input.value;
            }
            // Mettre à jour la variante et le prix
            this._updateVariantAndPrice();
            this.updatePreview();

            // Effet de fondu entrant
            if (visualsContainer) {
              visualsContainer.style.opacity = '1';
            }
          }
        };
        this._boundHandlers.couleurChanges.set(input, handler);
        input.addEventListener('change', handler);

        // Initialiser l'état et le libellé
        if (input.checked) {
          this.state.couleur_fond = input.value;
          if (selectedColorSpan) {
            const label = document.querySelector(`label[for="${input.id}"]`);
            selectedColorSpan.textContent = label ? label.getAttribute('title') || input.value : input.value;
          }
        }
      });
    }

    // Position et orientation de la flèche (si existe)
    const positionInputs = document.querySelectorAll(`input[name*="Position de la flèche"]`);
    positionInputs.forEach(input => {
      const handler = () => {
        if (input.checked) {
          this.state.arrow_position = input.value;
          this.updatePreview();
        }
      };
      this._boundHandlers.positionChanges.set(input, handler);
      input.addEventListener('change', handler);

      if (input.checked) {
        this.state.arrow_position = input.value;
      }
    });

    const orientationInputs = document.querySelectorAll(`input[name*="Orientation de la flèche"]`);
    orientationInputs.forEach(input => {
      const handler = () => {
        if (input.checked) {
          this.state.arrow_orientation = parseInt(input.value) || 0;
          this.updatePreview();
        }
      };
      this._boundHandlers.orientationChanges.set(input, handler);
      input.addEventListener('change', handler);

      if (input.checked) {
        this.state.arrow_orientation = parseInt(input.value) || 0;
      }
    });

    // Soumission du formulaire - intercepter pour formater les données
    if (this.elements.form) {
      this._boundHandlers.formSubmit = (e) => this._handleFormSubmit(e);
      this.elements.form.addEventListener('submit', this._boundHandlers.formSubmit);
    }
  }

  /**
   * Convertit l'angle en symbole de flèche pour l'affichage panier
   * @private
   */
  _convertAngleToArrow(angle) {
    // Correspondance avec les valeurs du template personnalisation-v2.liquid
    // Rotation horaire depuis → (flèche de base)
    const angleToArrow = {
      '0': '→',
      '45': '↘',
      '-45': '↗',
      '90': '↓',
      '-90': '↑',
      '135': '↙',
      '-135': '↖',
      '180': '←',
      '-180': '←'
    };
    return angleToArrow[angle] || angle;
  }

  /**
   * Convertit la position en français pour l'affichage panier
   * @private
   */
  _convertPositionToFrench(position) {
    const positionToFrench = {
      'after': 'Après le texte',
      'before': 'Avant le texte'
    };
    return positionToFrench[position] || position;
  }

  /**
   * Gère la soumission du formulaire avec formatage des données
   * @private
   */
  async _handleFormSubmit(e) {
    e.preventDefault();

    // Valider le formulaire
    if (!this.elements.textePrincipal || !this.elements.textePrincipal.value.trim()) {
      const errorEl = document.getElementById(`error-texte_principal-${this.sectionId}`);
      if (errorEl) errorEl.classList.add('active');
      return;
    }

    // Désactiver le bouton pendant la soumission (avec classe pour éviter le style rouge)
    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.disabled = true;
      this.elements.addToCartButton.classList.add('is-submitting');
      const span = this.elements.addToCartButton.querySelector('span');
      if (span) span.textContent = 'Ajout en cours...';
    }

    try {
      const formData = new FormData();

      // Obtenir l'ID de variante correct
      const hasVersoText = (this.state.texte_verso || '').trim().length > 0;
      const dimensionInputs = this.elements.dimensionInputs || [];
      const selectedDimensionInput = Array.from(dimensionInputs).find(input => input.checked);
      const selectedColor = (this.state.couleur_fond || 'Jaune').toLowerCase();

      if (!selectedDimensionInput) {
        throw new Error('Veuillez sélectionner une dimension');
      }

      const baseKey = selectedColor;
      const variantKey = hasVersoText ? `data-variant-${baseKey}-verso-id` : `data-variant-${baseKey}-id`;
      const variantId = selectedDimensionInput.getAttribute(variantKey);

      if (!variantId) {
        throw new Error('Variante introuvable pour cette configuration');
      }

      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Propriétés à exclure (déjà dans les variantes)
      const excludedProperties = ['Dimension', 'Couleur de fond'];

      // Collecter les propriétés du formulaire
      const allInputs = this.elements.form.querySelectorAll('input, select, textarea');
      allInputs.forEach(input => {
        if (input.name && input.name.startsWith('properties[') && input.value) {
          const propertyName = input.name.replace('properties[', '').replace(']', '');

          // Exclure les propriétés redondantes
          if (excludedProperties.includes(propertyName)) {
            return;
          }

          // Ne pas ajouter orientation/position pour format carré
          if (this.state.dimension === 'carré') {
            if (propertyName.includes('Orientation') || propertyName.includes('Position')) {
              return;
            }
          }

          // Convertir les valeurs d'orientation en flèches
          if (propertyName.includes('Orientation') && input.checked) {
            formData.append(input.name, this._convertAngleToArrow(input.value));
          }
          // Convertir les valeurs de position en français
          else if (propertyName.includes('Position') && input.checked) {
            formData.append(input.name, this._convertPositionToFrench(input.value));
          }
          // Pour les autres inputs
          else if ((input.type === 'radio' && input.checked) ||
                   (input.type !== 'radio' && input.value.trim())) {
            formData.append(input.name, input.value);
          }
        }
      });

      // Soumettre via AJAX
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        window.location.href = '/cart';
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erreur lors de l\'ajout au panier');
      }

    } catch (error) {
      console.error('[PanneauEditor] Erreur lors de l\'ajout au panier:', error);
      alert('Erreur lors de l\'ajout au panier: ' + error.message);
    } finally {
      if (this.elements.addToCartButton) {
        this.elements.addToCartButton.disabled = false;
        this.elements.addToCartButton.classList.remove('is-submitting');
        const span = this.elements.addToCartButton.querySelector('span');
        if (span) span.textContent = 'Ajouter au panier';
      }
    }
  }

  /**
   * Met à jour le preview avec debouncing (pour les saisies de texte)
   */
  updatePreviewDebounced() {
    clearTimeout(this.updateTimeout);
    this.updateTimeout = setTimeout(() => {
      this.updatePreview();
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Met à jour le preview immédiatement
   */
  updatePreview() {
    if (!this.initialized || !this.engine || !this.renderer) {
      console.warn('[PanneauEditor] Non initialisé');
      return;
    }

    // Vérifier que le renderer est complètement prêt (layer initialisé)
    if (!this.renderer.layer) {
      console.warn('[PanneauEditor] Renderer non prêt, layer non initialisé');
      return;
    }

    try {
      // 1. Formater le recto avec le moteur
      const formatResult = this.engine.format({
        texte_principal: this.state.texte_principal,
        texte_secondaire: this.state.texte_secondaire,
        dimension: this.state.dimension
      });

      // Valider le résultat du formatage
      if (!this._validateFormatResult(formatResult)) {
        this._showError('Erreur lors du formatage du texte');
        return;
      }

      // 2. Afficher le recto avec le renderer
      this.renderer.render(formatResult, {
        couleur_fond: this.state.couleur_fond,
        show_arrow: this.state.dimension === 'rectangle',
        arrow_position: this.state.arrow_position,
        arrow_orientation: this.state.arrow_orientation
      });

      // 3. Afficher le verso avec le renderer verso (si existe)
      let formatResultVerso = null;
      if (this.rendererVerso) {
        if (this.state.texte_verso) {
          formatResultVerso = this.engine.format({
            texte_principal: '',
            texte_secondaire: this.state.texte_verso,
            dimension: this.state.dimension
          });

          // Forcer la hauteur du texte verso à 7.5mm (identique pour tous les formats)
          formatResultVerso.secondary.height = 7.5;

          // Valider le résultat du formatage verso
          if (!this._validateFormatResult(formatResultVerso)) {
            this._showError('Erreur lors du formatage du texte verso');
            return;
          }

          this.rendererVerso.render(formatResultVerso, {
            couleur_fond: this.state.couleur_fond,
            show_arrow: false,  // Pas de flèche sur le verso
            arrow_position: 'after',
            arrow_orientation: 0,
            force_center: true,  // Toujours centrer le texte sur le verso
            show_background_svg: true  // Afficher le SVG de fond sur le verso
          });
        } else {
          // Afficher le verso vide avec le SVG de fond
          const emptyFormatResult = this.engine.format({
            texte_principal: '',
            texte_secondaire: '',
            dimension: this.state.dimension
          });

          // Valider le résultat du formatage verso vide
          if (!this._validateFormatResult(emptyFormatResult)) {
            this._showError('Erreur lors du formatage du verso vide');
            return;
          }

          this.rendererVerso.render(emptyFormatResult, {
            couleur_fond: this.state.couleur_fond,
            show_arrow: false,
            arrow_position: 'after',
            arrow_orientation: 0,
            force_center: true,
            show_background_svg: true  // Toujours afficher le SVG de fond sur le verso
          });
        }
      }

      // 4. Afficher les indicateurs après les deux panneaux
      this._updateIndicators(formatResult, formatResultVerso);

    } catch (error) {
      console.error('[PanneauEditor] Erreur lors de la mise à jour:', error);
      this._showError('Erreur lors du rendu du panneau');
    }
  }

  /**
   * Met à jour les character-counter avec les informations de formatage
   * @private
   */
  _updateIndicators(formatResult, formatResultVerso = null) {
    // Mettre à jour le character-counter du texte principal
    const principalCounter = document.getElementById(`counter-texte_principal-${this.sectionId}`);
    if (principalCounter && this.elements.textePrincipal) {
      const currentLength = this.elements.textePrincipal.value.length;
      const remaining = 40 - currentLength; // MAX_LENGTH = 40

      let counterText = '';
      if (remaining > 0) {
        counterText = `Encore ${remaining} caractère${remaining > 1 ? 's' : ''} disponible${remaining > 1 ? 's' : ''}`;
      } else {
        counterText = 'Limite atteinte';
      }

      // Ajouter les informations de formatage si le texte n'est pas vide
      if (this.elements.textePrincipal.value.trim()) {
        const infoItems = [];
        infoItems.push(`Hauteur des lettres ${formatResult.title.height}mm`);
        infoItems.push(`${formatResult.title.lines.length} ligne${formatResult.title.lines.length > 1 ? 's' : ''}`);

        counterText += ` • ${infoItems.join(' • ')}`;
      }

      principalCounter.textContent = counterText;
    }

    // Mettre à jour le character-counter du texte secondaire
    const secondaireCounter = document.getElementById(`counter-texte_secondaire-${this.sectionId}`);
    if (secondaireCounter && this.elements.texteSecondaire) {
      const currentLength = this.elements.texteSecondaire.value.length;
      const remaining = 40 - currentLength;

      let counterText = '';
      if (remaining > 0) {
        counterText = `Encore ${remaining} caractère${remaining > 1 ? 's' : ''} disponible${remaining > 1 ? 's' : ''}`;
      } else {
        counterText = 'Limite atteinte';
      }

      // Ajouter les informations de formatage si le texte secondaire existe
      if (formatResult.secondary.text && formatResult.secondary.text.trim()) {
        counterText += ` • Hauteur des lettres ${formatResult.secondary.height}mm`;
      }

      secondaireCounter.textContent = counterText;
    }

    // Mettre à jour le character-counter du texte verso
    const versoCounter = document.getElementById(`counter-texte_verso-${this.sectionId}`);
    if (versoCounter && this.elements.texteVerso) {
      const currentLength = this.elements.texteVerso.value.length;
      const remaining = 40 - currentLength;

      let counterText = '';
      if (remaining > 0) {
        counterText = `Encore ${remaining} caractère${remaining > 1 ? 's' : ''} disponible${remaining > 1 ? 's' : ''}`;
      } else {
        counterText = 'Limite atteinte';
      }

      // Ajouter les informations de formatage si le texte verso existe
      // Note: Le texte verso est formaté comme texte secondaire (plus petit)
      if (formatResultVerso && formatResultVerso.secondary.text && formatResultVerso.secondary.text.trim()) {
        counterText += ` • Hauteur des lettres ${formatResultVerso.secondary.height}mm`;
      }

      versoCounter.textContent = counterText;
    }
  }

  /**
   * Valide le résultat du moteur de formatage
   * @private
   * @param {Object} formatResult - Résultat de engine.format()
   * @returns {boolean} true si valide, false sinon
   */
  _validateFormatResult(formatResult) {
    if (!formatResult) {
      console.error('[PanneauEditor] Format result is null/undefined');
      return false;
    }

    if (!formatResult.title || typeof formatResult.title !== 'object') {
      console.error('[PanneauEditor] Format result missing valid title object');
      return false;
    }

    if (!formatResult.secondary || typeof formatResult.secondary !== 'object') {
      console.error('[PanneauEditor] Format result missing valid secondary object');
      return false;
    }

    return true;
  }

  /**
   * Affiche un message d'erreur
   * @private
   */
  _showError(message) {
    console.error('[PanneauEditor]', message);

    // Afficher visuellement dans le preview
    if (this.elements.previewContainer) {
      this.elements.previewContainer.innerHTML = `
        <div class="panneau-error">
          <p>❌ ${message}</p>
        </div>
      `;
    }
  }

  /**
   * Détruit le contrôleur et libère les ressources
   */
  destroy() {
    // Supprimer tous les event listeners pour éviter les fuites mémoire
    if (this.elements.textePrincipal && this._boundHandlers.textePrincipalInput) {
      this.elements.textePrincipal.removeEventListener('input', this._boundHandlers.textePrincipalInput);
    }

    if (this.elements.texteSecondaire && this._boundHandlers.texteSecondaireInput) {
      this.elements.texteSecondaire.removeEventListener('input', this._boundHandlers.texteSecondaireInput);
    }

    if (this.elements.texteVerso && this._boundHandlers.texteVersoInput) {
      this.elements.texteVerso.removeEventListener('input', this._boundHandlers.texteVersoInput);
    }

    // Supprimer les handlers des dimension inputs
    if (this._boundHandlers.dimensionChanges) {
      this._boundHandlers.dimensionChanges.forEach((handler, input) => {
        input.removeEventListener('change', handler);
      });
      this._boundHandlers.dimensionChanges.clear();
    }

    // Supprimer les handlers des couleur inputs
    if (this._boundHandlers.couleurChanges) {
      this._boundHandlers.couleurChanges.forEach((handler, input) => {
        input.removeEventListener('change', handler);
      });
      this._boundHandlers.couleurChanges.clear();
    }

    // Supprimer les handlers des position inputs
    if (this._boundHandlers.positionChanges) {
      this._boundHandlers.positionChanges.forEach((handler, input) => {
        input.removeEventListener('change', handler);
      });
      this._boundHandlers.positionChanges.clear();
    }

    // Supprimer les handlers des orientation inputs
    if (this._boundHandlers.orientationChanges) {
      this._boundHandlers.orientationChanges.forEach((handler, input) => {
        input.removeEventListener('change', handler);
      });
      this._boundHandlers.orientationChanges.clear();
    }

    // Détruire les renderers
    if (this.renderer) {
      this.renderer.destroy();
      this.renderer = null;
    }
    if (this.rendererVerso) {
      this.rendererVerso.destroy();
      this.rendererVerso = null;
    }

    // Nettoyer le timeout
    clearTimeout(this.updateTimeout);
    this.updateTimeout = null;

    // Marquer comme non initialisé
    this.initialized = false;
  }
}

// ============================================================================
// INITIALISATION AUTOMATIQUE
// ============================================================================

document.addEventListener('DOMContentLoaded', async () => {
  // Trouver la section de personnalisation
  const mainProductElement = document.querySelector('[data-section-id]');

  if (!mainProductElement) {
    console.warn('[PanneauEditor] Section de personnalisation non trouvée');
    return;
  }

  const sectionId = mainProductElement.getAttribute('data-section-id');

  // Vérifier que Konva est chargé
  if (typeof Konva === 'undefined') {
    console.error('[PanneauEditor] Konva.js non chargé !');
    console.error('Ajoutez <script src="https://unpkg.com/konva@9/konva.min.js"></script>');
    return;
  }

  // Vérifier que le moteur et le renderer sont chargés
  if (typeof RhinoFormattingEngine === 'undefined' || typeof PanneauRenderer === 'undefined') {
    console.error('[PanneauEditor] Moteur ou renderer non chargés !');
    return;
  }

  // Créer et initialiser le contrôleur
  const controller = new PanneauController(sectionId);
  const success = await controller.init();

  if (!success) {
    console.error('[PanneauEditor] Échec de l\'initialisation');
  } else {
    // Exposer globalement pour debug
    window.panneauController = controller;
  }
});

// Export
window.PanneauController = PanneauController;
